import { Link } from 'react-router-dom'
import { MetricCards } from '../components/MetricCards'
import { PageLoader } from '../components/PageLoader'
import { RecentOrders } from '../components/RecentOrders'
import { Spinner } from '../components/Spinner'
import { Topbar } from '../components/Topbar'
import { LOADING_LABELS } from '../constants/loading'
import { RECENT_ORDERS_LIMIT } from '../constants/orders'
import { APP_ROUTES } from '../constants/routes'
import { OVERVIEW_PAGE, OVERVIEW_PRODUCTS_LIMIT, VENDOR_BADGES } from '../constants/vendor'
import { displayAddress } from '../helpers/format'
import { buildVendorOverviewMetrics } from '../helpers/overview'
import { catalogProductName } from '../helpers/vendor'
import { useRecentOrders } from '../hooks/useRecentOrders'
import { useVendorData } from '../hooks/useVendorData'
import '../styles/overview-page.scss'
import '../styles/catalog-page.scss'

export function OverviewPage() {
  const { vendor, location, products, catalog, loading, error } = useVendorData(
    { includeCatalog: true },
  )
  const {
    orders,
    loading: ordersLoading,
    error: ordersError,
  } = useRecentOrders()
  if (loading) {
    return (
      <div className="overview-page">
        <Topbar title={OVERVIEW_PAGE.title} subtitle={OVERVIEW_PAGE.subtitle} />
        <PageLoader />
      </div>
    )
  }

  if (error || !vendor) {
    return (
      <div className="overview-page">
        <Topbar title={OVERVIEW_PAGE.title} subtitle={OVERVIEW_PAGE.subtitle} />
        <section className="panel" style={{ padding: 24 }}>
          <p style={{ color: 'var(--danger)' }}>
            {error || OVERVIEW_PAGE.notFound}
          </p>
        </section>
      </div>
    )
  }

  const metrics = buildVendorOverviewMetrics(orders)
  const listedProducts = products.slice(0, OVERVIEW_PRODUCTS_LIMIT)

  return (
    <div className="overview-page">
      <Topbar
        title={vendor.name}
        subtitle={`Status · ${vendor.status} · ${vendor.phone}`}
      />
      {ordersLoading ? (
        <Spinner block label={LOADING_LABELS.page} />
      ) : ordersError ? (
        <p className="table-empty">{ordersError}</p>
      ) : (
        <MetricCards metrics={metrics} />
      )}
      <div className="overview-page__grid">
        <RecentOrders
          orders={orders.slice(0, RECENT_ORDERS_LIMIT)}
          loading={ordersLoading}
          error={ordersError}
        />

        <div className="overview-page__side">
          <section className="panel">
            <div className="panel__header">
              <h2 className="panel__title">{OVERVIEW_PAGE.locationsTitle}</h2>
              <Link className="panel__link" to={APP_ROUTES.locations}>
                {location ? OVERVIEW_PAGE.manage : OVERVIEW_PAGE.addLocation}
              </Link>
            </div>
            {!location ? (
              <p className="table-empty">{OVERVIEW_PAGE.emptyLocations}</p>
            ) : (
              <div className="overview-side__body">
                <p className="overview-side__title">{location.name}</p>
                <p className="overview-side__meta">{displayAddress(location.address)}</p>
                <div className="overview-side__badges">
                  {location.supportsExpress ? (
                    <span className="status-badge status-badge--active">
                      {VENDOR_BADGES.express}
                    </span>
                  ) : (
                    <span className="status-badge status-badge--inactive">
                      {VENDOR_BADGES.standard}
                    </span>
                  )}
                  <span
                    className={`status-badge status-badge--${location.isActive ? 'active' : 'inactive'}`}
                  >
                    {location.isActive ? VENDOR_BADGES.active : VENDOR_BADGES.inactive}
                  </span>
                </div>
              </div>
            )}
          </section>
          <section className="panel">
            <div className="panel__header">
              <h2 className="panel__title">{OVERVIEW_PAGE.productsTitle}</h2>
              <Link className="panel__link" to={APP_ROUTES.inventory}>
                {OVERVIEW_PAGE.productsLink}
              </Link>
            </div>
            {listedProducts.length === 0 ? (
              <p className="table-empty">{OVERVIEW_PAGE.emptyProducts}</p>
            ) : (
              <ul className="low-stock-list">
                {listedProducts.map((item) => (
                  <li key={item.id} className="low-stock-list__item">
                    <div>
                      <p className="low-stock-list__title">
                        {catalogProductName(catalog, item.productId)}
                      </p>
                    </div>
                    <span
                      className={`status-badge status-badge--${item.isActive ? 'active' : 'inactive'}`}
                    >
                      {item.isActive
                        ? VENDOR_BADGES.active
                        : VENDOR_BADGES.inactive}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  )
}
